import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Medal, 
  Crown, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Globe,
  Flag,
  Users,
  Calendar,
  BarChart,
  Search,
  Filter,
  ExternalLink
} from 'lucide-react';
import { LeaderboardEntry } from '../types';
import { useUser } from '../context/UserContext';
import { leaderboardAPI } from '../utils/apiClient';
import FlagIcon from '../components/FlagIcon';
import { useFlagCache } from '../hooks/useFlagCache';

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const { state: userState } = useUser();
  const { preloadLeaderboardFlags } = useFlagCache();
  const [selectedCategory, setSelectedCategory] = useState<'wpm' | 'accuracy' | 'battles' | 'xp'>('wpm');
  const [selectedTimeframe, setSelectedTimeframe] = useState<'daily' | 'weekly' | 'monthly' | 'alltime'>('weekly');
  const [selectedScope, setSelectedScope] = useState<'global' | 'country' | 'friends'>('global');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // Helper functions to avoid nested ternaries
  const getCategoryColor = (category: string): string => {
    if (category === 'wpm') return 'text-accent';
    if (category === 'accuracy') return 'text-green-500';
    if (category === 'battles') return 'text-blue-500';
    return 'text-purple-500';
  };

  const getScopeLabel = (scope: string): string => {
    if (scope === 'global') return 'Global';
    if (scope === 'country') return 'Your Country';
    return 'Friends';
  };

  const getChangeColor = (change: number): string => {
    if (change > 0) return 'text-green-500';
    if (change < 0) return 'text-red-500';
    return 'text-textMuted';
  };

  const getUserStatValue = (category: string): number => {
    if (category === 'wpm') return userState.user?.stats.bestWpm || 0;
    if (category === 'accuracy') return userState.user?.stats.bestAccuracy || 0;
    if (category === 'battles') return userState.user?.stats.battlesWon || 0;
    return userState.user?.totalXp || 0;
  };

  // Find the current user's entry in the loaded leaderboard data
  const currentUserEntry = leaderboardData.find(
    entry => entry.userId === userState.user?.id
  );

  const getRowHighlight = (isCurrentUser: boolean, rank: number): string => {
    if (isCurrentUser) return 'bg-accent/10 border-l-2 border-accent';
    if (rank <= 3) return 'bg-accent/5';
    return '';
  };

  const getScopeIcon = (scope: string) => {
    if (scope === 'global') return <Globe className="w-4 h-4 mr-1" />;
    if (scope === 'country') return <Flag className="w-4 h-4 mr-1" />;
    return <Users className="w-4 h-4 mr-1" />;
  };

  // Fetch leaderboard data from API
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        let data: any[];
        
        // Fetch based on scope
        if (selectedScope === 'global') {
          data = await leaderboardAPI.get(selectedCategory, 100);
        } else if (selectedScope === 'country' && userState.user?.country) {
          data = await leaderboardAPI.getByCountry(userState.user.country, selectedCategory, 100);
        } else {
          // Friends scope - would need a separate API endpoint
          // For now, fall back to global
          data = await leaderboardAPI.get(selectedCategory, 100);
        }
        
        // Transform API data to match LeaderboardEntry format
        const transformedData: LeaderboardEntry[] = data.map((entry, index) => {
          let value: number;
          if (selectedCategory === 'wpm') {
            value = entry.wpm;
          } else if (selectedCategory === 'accuracy') {
            value = entry.accuracy;
          } else if (selectedCategory === 'battles') {
            value = entry.battlesWon || 0;
          } else {
            value = entry.xp || 0;
          }
          
          return {
            rank: index + 1,
            userId: entry.userId,
            username: entry.username,
            value,
            country: entry.country,
            change: 0, // Would need historical data to calculate change
          };
        });
        
        // Filter by search term if present
        const filteredData = searchTerm.trim()
          ? transformedData.filter(entry => 
              entry.username.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : transformedData;
        
        // Preload flags for better performance
        preloadLeaderboardFlags(filteredData);
        
        setLeaderboardData(filteredData);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        // Fallback to empty array on error
        setLeaderboardData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedTimeframe, selectedScope, searchTerm]);

  const categories = [
    { id: 'wpm', label: 'WPM', icon: TrendingUp, unit: '' },
    { id: 'accuracy', label: 'Accuracy', icon: Trophy, unit: '%' },
    { id: 'battles', label: 'Battles Won', icon: Medal, unit: '' },
    { id: 'xp', label: 'Experience', icon: Crown, unit: ' XP' },
  ] as const;

  const timeframes = [
    { id: 'daily', label: 'Today', icon: Calendar },
    { id: 'weekly', label: 'This Week', icon: BarChart },
    { id: 'monthly', label: 'This Month', icon: BarChart },
    { id: 'alltime', label: 'All Time', icon: BarChart },
  ] as const;

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-textMuted font-bold">{rank}</span>;
    }
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-textMuted" />;
  };

  const formatValue = (value: number, category: string) => {
    switch (category) {
      case 'accuracy':
        return `${value.toFixed(1)}%`;
      case 'xp':
        return value.toLocaleString();
      default:
        return Math.round(value).toString();
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 pb-10">
      <motion.div 
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold mb-2 flex items-center justify-center">
          <Trophy className="w-8 h-8 mr-3 text-accent" />
          Leaderboard
        </h1>
        <p className="text-textMuted">See how you rank against the best typers</p>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 mb-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {/* Search and Filter Bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-textMuted" />
            </div>
            <input
              type="text"
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full bg-secondary border border-border/20 rounded-lg text-text placeholder-textMuted focus:border-accent focus:outline-none"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              className="flex items-center ml-2 p-2 bg-secondary rounded-lg hover:bg-accent/10 transition-colors border border-border/20"
            >
              <Filter className="h-4 w-4 text-textMuted" />
              <span className="ml-2 hidden md:inline">Filters</span>
            </button>
            
            {filterMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-cardBg rounded-lg shadow-lg border border-border/30 z-10 p-3">
                <div className="mb-3">
                  <h4 className="text-sm font-medium mb-2">Category</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setFilterMenuOpen(false);
                        }}
                        className={`flex items-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap ${
                          selectedCategory === category.id
                            ? 'bg-accent text-primary'
                            : 'text-textMuted hover:bg-secondary'
                        }`}
                      >
                        <category.icon className="w-3 h-3 flex-shrink-0" />
                        <span>{category.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mb-3">
                  <h4 className="text-sm font-medium mb-2">Timeframe</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {timeframes.map(timeframe => (
                      <button
                        key={timeframe.id}
                        onClick={() => {
                          setSelectedTimeframe(timeframe.id);
                          setFilterMenuOpen(false);
                        }}
                        className={`flex items-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap ${
                          selectedTimeframe === timeframe.id
                            ? 'bg-accent text-primary'
                            : 'text-textMuted hover:bg-secondary'
                        }`}
                      >
                        <timeframe.icon className="w-3 h-3 flex-shrink-0" />
                        <span>{timeframe.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium mb-2">Scope</h4>
                  <div className="grid grid-rows-2 grid-cols-2 gap-1">
                    <button
                      key="global"
                      onClick={() => {
                        setSelectedScope('global');
                        setFilterMenuOpen(false);
                      }}
                      className={`flex items-center justify-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap ${
                        selectedScope === 'global'
                          ? 'bg-accent text-primary'
                          : 'text-textMuted hover:bg-secondary'
                      }`}
                    >
                      <Globe className="w-3 h-3 flex-shrink-0" />
                      <span>Global</span>
                    </button>
                    
                    <button
                      key="friends"
                      onClick={() => {
                        setSelectedScope('friends');
                        setFilterMenuOpen(false);
                      }}
                      className={`flex items-center justify-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap ${
                        selectedScope === 'friends'
                          ? 'bg-accent text-primary'
                          : 'text-textMuted hover:bg-secondary'
                      }`}
                    >
                      <Users className="w-3 h-3 flex-shrink-0" />
                      <span>Friends</span>
                    </button>
                    
                    <button
                      key="country"
                      onClick={() => {
                        setSelectedScope('country');
                        setFilterMenuOpen(false);
                      }}
                      className={`flex items-center justify-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap ${
                        selectedScope === 'country'
                          ? 'bg-accent text-primary'
                          : 'text-textMuted hover:bg-secondary'
                      }`}
                    >
                      <Flag className="w-3 h-3 flex-shrink-0" />
                      <span>Country</span>
                    </button>
                    
                    <div className="hidden">
                      {/* Empty placeholder to create the 2x2 grid layout */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Category Filter */}
          <div>
            <div className="block text-sm font-medium mb-3" aria-label="Category">Category</div>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(category => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap min-w-[120px] ${
                      selectedCategory === category.id
                        ? 'bg-accent text-primary font-medium shadow-md'
                        : 'bg-secondary text-textMuted hover:text-accent border border-border/20'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timeframe Filter */}
          <div>
            <div className="block text-sm font-medium mb-3" aria-label="Timeframe">Timeframe</div>
            <div className="grid grid-cols-2 gap-2">
              {timeframes.map(timeframe => (
                <button
                  key={timeframe.id}
                  onClick={() => setSelectedTimeframe(timeframe.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap min-w-[120px] ${
                    selectedTimeframe === timeframe.id
                      ? 'bg-accent text-primary font-medium shadow-md'
                      : 'bg-secondary text-textMuted hover:text-accent border border-border/20'
                  }`}
                >
                  <timeframe.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{timeframe.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Scope Filter */}
          <div>
            <div className="block text-sm font-medium mb-3" aria-label="Scope">Scope</div>
            <div className="grid grid-rows-2 grid-cols-2 gap-2">
              <button
                key="global"
                onClick={() => setSelectedScope('global')}
                className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap min-w-[90px] ${
                  selectedScope === 'global'
                    ? 'bg-accent text-primary font-medium shadow-md'
                    : 'bg-secondary text-textMuted hover:text-accent border border-border/20'
                }`}
              >
                <Globe className="w-4 h-4 flex-shrink-0" />
                <span>Global</span>
              </button>
              
              <button
                key="friends"
                onClick={() => setSelectedScope('friends')}
                className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap min-w-[90px] ${
                  selectedScope === 'friends'
                    ? 'bg-accent text-primary font-medium shadow-md'
                    : 'bg-secondary text-textMuted hover:text-accent border border-border/20'
                }`}
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>Friends</span>
              </button>
              
              <button
                key="country"
                onClick={() => setSelectedScope('country')}
                className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap min-w-[90px] ${
                  selectedScope === 'country'
                    ? 'bg-accent text-primary font-medium shadow-md'
                    : 'bg-secondary text-textMuted hover:text-accent border border-border/20'
                }`}
              >
                <Flag className="w-4 h-4 flex-shrink-0" />
                <span>Country</span>
              </button>
              
              <div className="hidden">
                {/* Empty placeholder to create the 2x2 grid layout */}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Leaderboard */}
      <motion.div 
        className="bg-cardBg rounded-xl overflow-hidden shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {/* Header */}
        <div className="bg-secondary px-6 py-4 border-b border-border/30">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center">
              <span className={`mr-2 ${getCategoryColor(selectedCategory)}`}>
                {categories.find(c => c.id === selectedCategory)?.icon && React.createElement(categories.find(c => c.id === selectedCategory)!.icon, { className: 'w-5 h-5 mr-2' })}
              </span>
              {categories.find(c => c.id === selectedCategory)?.label} Leaderboard
            </h2>
            <div className="text-sm text-textMuted flex items-center">
              {getScopeIcon(selectedScope)}
              <span>{getScopeLabel(selectedScope)}</span>
              <span className="mx-1">•</span>
              <Calendar className="w-4 h-4 mr-1" />
              <span>{timeframes.find(t => t.id === selectedTimeframe)?.label}</span>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-textMuted">Loading leaderboard...</p>
          </div>
        )}

        {/* Leaderboard Entries */}
        {!loading && (
          <div>
            {leaderboardData.length > 0 ? (
              <AnimatePresence>
                <div className="divide-y divide-border/10">
                  {leaderboardData.map((entry, index) => (
                    <motion.div
                      key={entry.userId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      className={`px-6 py-4 flex items-center justify-between hover:bg-secondary/50 transition-colors cursor-pointer ${getRowHighlight(entry.userId === userState.user?.id, entry.rank)}`}
                      onClick={() => navigate(`/profile/${entry.userId}`)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 flex justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          {selectedScope !== 'friends' && entry.country && (
                            <FlagIcon countryCode={entry.country} size="md" />
                          )}
                          <div>
                            <div className="font-medium text-text flex items-center">
                              {entry.username}
                              {entry.userId === userState.user?.id && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-accent text-primary rounded font-bold">You</span>
                              )}
                              <ExternalLink className="w-3 h-3 ml-1.5 text-textMuted" />
                            </div>
                            {selectedScope === 'country' && entry.country && (
                              <div className="text-xs text-textMuted flex items-center mt-1">
                                <span className="mr-1">From</span>
                                <FlagIcon countryCode={entry.country} size="sm" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className={`font-bold text-lg ${getCategoryColor(selectedCategory)}`}>
                            {formatValue(entry.value, selectedCategory)}
                            <span className="text-xs ml-1 text-textMuted">
                              {selectedCategory === 'wpm' ? 'wpm' : ''}
                            </span>
                          </div>
                          {entry.change !== 0 && (
                            <div className="flex items-center justify-end space-x-1 text-sm">
                              {getChangeIcon(entry.change)}
                              <span className={getChangeColor(entry.change)}>
                                {Math.abs(entry.change)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>
            ) : (
              <div className="py-10 text-center">
                <Search className="w-12 h-12 mx-auto mb-3 text-textMuted opacity-50" />
                <p className="text-textMuted">No results found</p>
                <p className="text-sm text-textMuted">Try changing your search or filters</p>
              </div>
            )}
          </div>
        )}

        {/* Your Rank — pinned at bottom, only when logged in */}
        {!loading && leaderboardData.length > 0 && userState.isAuthenticated && (
          <motion.div 
            className="bg-accent/10 border-t-2 border-accent px-6 py-4 cursor-pointer hover:bg-accent/20 transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            onClick={() => navigate(`/profile/${userState.user?.id}`)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-8 flex justify-center">
                  {currentUserEntry ? (
                    <span className="text-accent font-bold">#{currentUserEntry.rank}</span>
                  ) : (
                    <span className="text-textMuted font-bold text-sm">—</span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <FlagIcon countryCode={userState.user?.country || 'US'} size="md" />
                  <div>
                    <div className="font-medium text-accent flex items-center">
                      You ({userState.user?.username || 'Guest'})
                      <ExternalLink className="w-3 h-3 ml-1.5 text-accent/60" />
                    </div>
                    <div className="text-xs text-textMuted">
                      {currentUserEntry ? 'Your current rank' : 'Complete a test to appear on the leaderboard'}
                    </div>
                  </div>
                </div>
              </div>
              <div className={`font-bold text-lg ${getCategoryColor(selectedCategory)}`}>
                {currentUserEntry
                  ? formatValue(currentUserEntry.value, selectedCategory)
                  : formatValue(getUserStatValue(selectedCategory), selectedCategory)}
                <span className="text-xs ml-1 text-textMuted">
                  {selectedCategory === 'wpm' ? 'wpm' : ''}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Personal Stats */}
      {userState.isAuthenticated && (
        <motion.div 
          className="mt-6 bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h3 className="text-lg font-semibold mb-4">Your Performance</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-secondary rounded-lg p-3 border border-border/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-textMuted">Best WPM</span>
                <TrendingUp className="w-4 h-4 text-accent" />
              </div>
              <div className="text-xl font-bold text-accent">
                {userState.user?.stats.bestWpm || 0}
              </div>
            </div>
            <div className="bg-secondary rounded-lg p-3 border border-border/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-textMuted">Accuracy</span>
                <Trophy className="w-4 h-4 text-green-500" />
              </div>
              <div className="text-xl font-bold text-green-500">
                {(userState.user?.stats.avgAccuracy || 0).toFixed(1)}%
              </div>
            </div>
            <div className="bg-secondary rounded-lg p-3 border border-border/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-textMuted">Tests</span>
                <BarChart className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-xl font-bold text-blue-500">
                {userState.user?.stats.totalTests || 0}
              </div>
            </div>
            <div className="bg-secondary rounded-lg p-3 border border-border/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-textMuted">Level</span>
                <Crown className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-xl font-bold text-purple-500">
                {userState.user?.level || 1}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Leaderboard;
